import * as React from 'react'
import { Params, redirect } from 'react-router-dom'

import { PerformanceReviewsEmployeeReviewCrumbDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'
import { ResolvableMeta } from 'modules/core/lib/navigation/routes'
import { Meta } from 'modules/core/lib/navigation/types'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getGroupName } from '../lib/getGroupName'
import questionGroups from '../store/collections/questionGroups'

const EmployeeReviewsIndexPage = React.lazy(
  () => import('modules/reviews/pages/employees/EmployeeReviewsIndexPage')
)
const EmployeeReviewPage = React.lazy(
  () => import('modules/reviews/pages/employees/Review/EmployeeReviewPage')
)
const ReviewAnalyticsPage = React.lazy(
  () => import('modules/reviews/pages/employees/Review/ReviewAnalyticsPage')
)
const ReviewAnswersPage = React.lazy(
  () => import('modules/reviews/pages/employees/Review/ReviewAnswersPage')
)
const SplitViewAnswersPage = React.lazy(
  () => import('modules/reviews/pages/employees/Review/SplitViewAnswersPage')
)
const ReviewQuestionGroupPage = React.lazy(
  () => import('modules/reviews/pages/employees/ReviewQuestionGroupPage')
)
const RootPage = React.lazy(() => import('modules/reviews/pages/employees/RootPage'))

const getReviewPageMeta: ResolvableMeta<Meta> = ({ watchQuery, params }) => {
  return watchQuery({
    query: PerformanceReviewsEmployeeReviewCrumbDocument,
    variables: { id: Number(params.review_id) },
  }).map(({ data }) => ({
    title: `${
      data.performanceReviews.review?.name ?? i18n.t('performance_process.reviews.untitled')
    }`,
    unsafeDisableNewLayout: true, // TODO: Remove it when we migrate the parent page to ResponsivePage
  }))
}

const redirectToReviewAnswers = ({
  params,
  navigation,
}: {
  params: Params
  navigation: Navigation<NavigationPaths>
}) => {
  return redirect(
    navigation.employees.id.reviews.reviewId.answers.fullPath({
      id: String(params.id),
      review_id: Number(params.review_id),
    })
  )
}

const employeesRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.employees.id.reviews.path,
      element: <RootPage />,
      handle: {
        meta: () => ({
          title: i18n.t('surveys.employee.performance.title'),
          description: i18n.t('surveys.employee.performance.description'),
          illustration: 'surveys',
          unsafeDisableNewLayout: true, // TODO: Remove it when we migrate the parent page to ResponsivePage
        }),
      },
      children: [
        {
          element: <EmployeeReviewsIndexPage />,
          index: true,
        },
        {
          path: navigation.employees.id.reviews.reviewId.path,
          element: <EmployeeReviewPage />,
          handle: {
            meta: getReviewPageMeta,
          },
          children: [
            {
              index: true,
              loader: ({ params }) => redirectToReviewAnswers({ params, navigation }),
            },
            {
              path: navigation.employees.id.reviews.reviewId.answers.path,
              element: <ReviewAnswersPage />,
            },
            {
              path: navigation.employees.id.reviews.reviewId.analytics.path,
              element: <ReviewAnalyticsPage />,
            },
          ],
        },
        {
          path: navigation.employees.id.reviews.answer.path,
          handle: {
            meta: getReviewPageMeta,
          },
          children: [
            {
              index: true,
              loader: ({ params }) => redirectToReviewAnswers({ params, navigation }),
            },
            {
              path: navigation.employees.id.reviews.answer.splitView.path,
              element: <SplitViewAnswersPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('surveys.review.reviews.split_view.title'),
                  illustration: 'user-interaction-review_chart',
                  description: i18n.t('surveys.review.reviews.split_view.description'),
                  unsafeDisableNewLayout: true, // TODO: Remove it when we migrate the parent page to ResponsivePage
                }),
              },
            },
            {
              path: navigation.employees.id.reviews.answer.questionGroupId.path,
              element: <ReviewQuestionGroupPage />,
              loader: ({ params }) => {
                return questionGroups.fetch({
                  data: {
                    surveys_form_id: Number(params.review_id),
                  },
                })
              },
              handle: {
                meta: ({ params }) => {
                  const questionGroup = questionGroups.get(Number(params.question_group_id))
                  return {
                    title: questionGroup ? getGroupName(questionGroup.respondentGroup) : '',
                    unsafeDisableNewLayout: true, // TODO: Remove it when we migrate the parent page to ResponsivePage
                  }
                },
              },
            },
          ],
        },
      ],
    },
  ]
}

export default employeesRoutes
